/**
 * Helpers for reading `unknown` JSON without ever casting to `any`. Used at the
 * storage boundary and on the RCU's replies, where the shape of the data is a
 * runtime question.
 */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const jsonText = (value: unknown): string => (typeof value === 'string' ? value : '');

export const jsonNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Whole numbers only: the firmware casts JSON numbers to bytes. */
export const jsonInt = (value: unknown): number | null => {
  const number = jsonNumber(value);
  return number !== null && Number.isInteger(number) ? number : null;
};

/** Absent or non-boolean reads as false; the firmware always sends a real one. */
export const jsonBoolean = (value: unknown): boolean => value === true;

/** A hex string such as the `dintype` in a reply, as a number. */
export const jsonHex = (value: unknown): number | null => {
  if (typeof value !== 'string') return null;
  const parsed = Number.parseInt(value, 16);
  return Number.isFinite(parsed) ? parsed : null;
};

export const jsonArray = (value: unknown): readonly unknown[] => (Array.isArray(value) ? value : []);

/** Numbers from an array, dropping anything else and keeping the order. */
export const jsonNumbers = (value: unknown): readonly number[] =>
  jsonArray(value).flatMap((item) => {
    const number = jsonNumber(item);
    return number === null ? [] : [number];
  });

export const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    // Unparseable input is a reply the app cannot use, not a crash.
    return null;
  }
};

/** Compact JSON, the way the firmware's own examples are written. */
export const toJson = (value: unknown): string => JSON.stringify(value);

/** Size of a document on the wire, in UTF-8 bytes. */
export const jsonBytes = (value: unknown): number => new TextEncoder().encode(toJson(value)).length;
